/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import { Scenario }            from '@/models/scenario/scenario';
import { WorkBlock }           from '@/models/scenario/work.block';
import { DiversionEditor }     from '@/screens/ide/diversion/diversion.editor';

import * as $                  from 'jquery';

import 'select2';

import KeyUpEvent = JQuery.KeyUpEvent;
import KeyDownEvent = JQuery.KeyDownEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class DiversionWorkEditor
 *
 * @brief dynamic dropdown list for picking which WorkBlock this diversion is for.
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class DiversionWorkEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the selectable values (decision points) */
  public values: WorkBlock[] = [];

  /** the selected milepost */
  public value: WorkBlock = null;

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListenter: (e: MouseEvent) => void = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    this.editingDone = params.stopEditing;

    this.value       = null;
    this.values      = [];

    /* pre-select value if there is an existing decision point selected already */

    if (params.value instanceof WorkBlock) {

      this.value = params.value;
    }

    /* try to figure out what mileposts we can even select from */

    const editor: DiversionEditor = params.context?.diversion;

    if (editor) {

      const scenario: Scenario = editor.getScenario();

      if (scenario) {

        this.values = Array.from(scenario.workBlocks().values());

      }
    }

    /* set up the Select2 drop down ... */

    this.gui   = document.createElement('div');
    this.gui.tabIndex = 0;

    if (!this.values || (this.values.length === 0)) {

      /* nothing to do, we don't have any work blocks */
      return ;
    }

    const data: any[] = [];

    /* walk the work blocks, generating a select option for each */

    for (const wb of this.values) {

      /* build this option ... */

      const name = '#' + wb._id + ' ' + wb._name.trim().toUpperCase();
      let selected: boolean = false;

      /* is this the current value? */

      if (this.value instanceof WorkBlock) {

        selected = wb._id === this.value._id;
      }

      /* add the option */

      data.push({
        id: `dp-${wb._id}`,
        text: name,
        name,
        wb,
        selected
      });

    }

    /* insert new HTML for our custom select ... */

    const html = `<select class="diversion-work-select"><option></option></select>`;
    $(this.gui).html(html);

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    const el: JQuery = $(this.gui).find('select.diversion-work-select');

    $(el).on('select2:close', (e) => {



    });

    const ce = this;

    $(el).on('select2:select', (e) => {

      const datum: any    = e.params.data;

      /* update the current value */

      ce.value = datum.wb;

      this.editingDone(true);
    });

    $(el).select2({

      placeholder: `Select...`,

      allowClear: false,
      closeOnSelect: true,
      width: '200px',

      data,

    });

    $(el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).css('width', 'auto');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

      $(selBox).trigger('focus');

    });

    setTimeout(() => {

      $(el).select2('open');

      $(ce.gui).on('keyup', (e: KeyUpEvent) => {

        if (e.key !== 'Tab') {

          return ;
        }

        ce.editingDone(true);

      });

      $(ce.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        ce.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    this.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === this.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', ce.clickListenter);

      this.editingDone(true);
    };

    document.addEventListener('click', ce.clickListenter);
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): WorkBlock {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.diversion-work-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined  {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    /* if there are no decision points...nothing we can do */

    return this.values.length === 0;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
